import { useEffect, useMemo } from "react";
import type { NavigateFunction } from "react-router-dom";

import type { PresetFile } from "../skill-presets";
import { useSkillCollectionEditorStore } from "../store";
import { getNormalizedServerTreePaths } from "../utils/tree-utils";

import type { SkillCollectionTreeNode } from "@/queries/skill-collections";

interface UseSkillCollectionEditorSyncOptions {
  serverTree: SkillCollectionTreeNode[];
  rootPath?: string | null;
  fileContents?: Record<string, string> | null;
  isSaving: boolean;
  presetFiles?: PresetFile[];
  pathname: string;
  navigate: NavigateFunction;
  onDirtyChange?: (dirty: boolean) => void;
}

export const useSkillCollectionEditorSync = ({
  serverTree,
  rootPath,
  fileContents,
  isSaving,
  presetFiles,
  pathname,
  navigate,
  onDirtyChange,
}: UseSkillCollectionEditorSyncOptions) => {
  const store = useSkillCollectionEditorStore();

  const { filePaths, folderPaths } = useMemo(
    () => getNormalizedServerTreePaths(serverTree, rootPath),
    [rootPath, serverTree],
  );

  useEffect(() => {
    if (isSaving) {
      return;
    }

    store.syncPersistedTree(filePaths, folderPaths, fileContents ?? undefined);
  }, [filePaths, fileContents, folderPaths, isSaving, store]);

  useEffect(() => {
    if (!presetFiles?.length) {
      return;
    }

    const firstPath = store.applyPresetFiles(presetFiles, rootPath);
    if (firstPath === null) {
      return;
    }

    navigate(pathname, { replace: true, state: {} });
  }, [navigate, pathname, presetFiles, rootPath, store]);

  useEffect(() => {
    onDirtyChange?.(store.isDirty);
  }, [onDirtyChange, store.isDirty]);
};
